import api from './api';

/**
 * Achievement service for managing achievements and user progress
 */

export const achievementsService = {
  /**
   * Get all achievement definitions
   * @param {Object} filters - Optional filters (category, rarity)
   * @returns {Promise<Object>} - { success: boolean, data: Array, error: string }
   */
  getAchievements: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.rarity) params.append('rarity', filters.rarity);
      
      const queryString = params.toString();
      const url = queryString ? `/api/achievements/?${queryString}` : '/api/achievements/';
      
      const response = await api.get(url);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get achievements error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch achievements',
        data: []
      };
    }
  },

  /**
   * Get a specific achievement definition
   * @param {string} achievementId - Achievement ID
   * @returns {Promise<Object>} - { success: boolean, data: Object, error: string }
   */
  getAchievement: async (achievementId) => {
    try {
      const response = await api.get(`/api/achievements/${achievementId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get achievement error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch achievement',
        data: null
      };
    }
  },

  /**
   * Get user's achievements with progress
   * @param {string} userId - User ID
   * @param {boolean} unlockedOnly - If true, only return unlocked achievements
   * @returns {Promise<Object>} - { success: boolean, data: Array, error: string }
   */
  getUserAchievements: async (userId, unlockedOnly = false) => {
    try {
      const url = unlockedOnly 
        ? `/api/achievements/user/${userId}?unlocked_only=true`
        : `/api/achievements/user/${userId}`;
      
      const response = await api.get(url);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get user achievements error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch user achievements',
        data: []
      };
    }
  },

  /**
   * Get recent achievement unlocks for a user
   * @param {string} userId - User ID
   * @param {number} limit - Maximum number of achievements to return
   * @returns {Promise<Object>} - { success: boolean, data: Array, error: string }
   */
  getRecentAchievements: async (userId, limit = 5) => {
    try {
      const response = await api.get(`/api/achievements/user/${userId}/recent?limit=${limit}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get recent achievements error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch recent achievements',
        data: []
      };
    }
  },

  /**
   * Get total achievement points for a user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - { success: boolean, data: Object, error: string }
   */
  getUserPoints: async (userId) => {
    try {
      const response = await api.get(`/api/achievements/user/${userId}/points`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get user points error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch user points',
        data: { total_points: 0 }
      };
    }
  },

  // Admin functions

  /**
   * Create a new achievement definition (Admin only)
   * @param {Object} achievementData - Achievement definition data
   * @returns {Promise<Object>} - { success: boolean, data: Object, error: string }
   */
  createAchievement: async (achievementData) => {
    try {
      const response = await api.post('/api/achievements/', achievementData);
      return { success: true, data: response.data, message: 'Achievement created successfully' };
    } catch (error) {
      console.error('Create achievement error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create achievement'
      };
    }
  },

  /**
   * Update an achievement definition (Admin only)
   * @param {string} achievementId - Achievement ID
   * @param {Object} achievementData - Updated achievement data
   * @returns {Promise<Object>} - { success: boolean, data: Object, error: string }
   */
  updateAchievement: async (achievementId, achievementData) => {
    try {
      const response = await api.put(`/api/achievements/${achievementId}`, achievementData);
      return { success: true, data: response.data, message: 'Achievement updated successfully' };
    } catch (error) {
      console.error('Update achievement error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update achievement'
      };
    }
  },

  /**
   * Delete an achievement definition (Admin only)
   * @param {string} achievementId - Achievement ID
   * @returns {Promise<Object>} - { success: boolean, message: string, error: string }
   */
  deleteAchievement: async (achievementId) => {
    try {
      const response = await api.delete(`/api/achievements/${achievementId}`);
      return { success: true, message: response.data.message || 'Achievement deleted successfully' };
    } catch (error) {
      console.error('Delete achievement error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete achievement'
      };
    }
  },

  /**
   * Manually award an achievement to a user (Admin only)
   * @param {string} userId - User ID
   * @param {string} achievementId - Achievement ID
   * @param {string} awardedBy - Admin user ID
   * @returns {Promise<Object>} - { success: boolean, data: Object, error: string }
   */
  awardAchievement: async (userId, achievementId, awardedBy) => {
    try {
      const response = await api.post('/api/achievements/award', {
        user_id: userId,
        achievement_id: achievementId,
        awarded_by: awardedBy
      });
      return { success: true, data: response.data, message: 'Achievement awarded successfully' };
    } catch (error) {
      console.error('Award achievement error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to award achievement'
      };
    }
  },

  /**
   * Revoke an achievement from a user (Admin only)
   * @param {string} userId - User ID
   * @param {string} achievementId - Achievement ID
   * @returns {Promise<Object>} - { success: boolean, message: string, error: string }
   */
  revokeAchievement: async (userId, achievementId) => {
    try {
      const response = await api.delete('/api/achievements/revoke', {
        data: {
          user_id: userId,
          achievement_id: achievementId
        }
      });
      return { success: true, message: response.data.message || 'Achievement revoked successfully' };
    } catch (error) {
      console.error('Revoke achievement error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to revoke achievement'
      };
    }
  },

  /**
   * Get global achievement statistics (Admin only)
   * @returns {Promise<Object>} - { success: boolean, data: Object, error: string }
   */
  getStatistics: async () => {
    try {
      const response = await api.get('/api/achievements/stats/global');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get statistics error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch statistics',
        data: null
      };
    }
  },

  /**
   * Seed all 50 achievement definitions (Admin only)
   * @param {boolean} force - If true, clear existing achievements and reseed
   * @returns {Promise<Object>} - { success: boolean, data: Object, error: string }
   */
  seedAchievements: async (force = false) => {
    try {
      const url = force ? '/api/achievements/seed?force=true' : '/api/achievements/seed';
      const response = await api.post(url);
      return { success: true, data: response.data, message: response.data.message };
    } catch (error) {
      console.error('Seed achievements error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to seed achievements'
      };
    }
  }
};

export default achievementsService;

